import { useEffect, useState } from "react";
import { constants } from "@qgiv/core-js";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { asyncSessionStorage, getSessionStorageKey } from "@qgiv/core-donor";
import {
    submitRegistration,
    prepareValuesForP2PRegistrationSubmit,
} from "@qgiv/p2p-registration";
import { getInitialValues } from "../../Pages/PaymentPage/formik";

const EarlyRegistrationSubmit = ({
    baseUrl,
    settings = {},
    classification = {},
    discounts = {},
    promoCodes = [],
    registrations = [],
    formId,
    storeCart = [],
    parentRegistrationId = "0",
    shippingMethodAmount = 0,
    setAppHasSubmittedRegistration,
    setSubmitting,
    fetchReceipt,
    sessionStorageReduxData,
    setErroredEntities,
    setOutdated,
    updateCurrentPage,
    updateRegistrationAccount,
    setErrors,
    totals = {},
}) => {
    const {
        ENUMS: {
            DefaultView: { STANDARD_REGISTRATION_RECEIPT },
        },
    } = constants;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [toReceipt, setToReceipt] = useState(false);
    const [receiptId, setReceiptId] = useState(null);

    // Need to bring in initial values from payment page
    const paymentValues = getInitialValues(settings, {});

    // Instead of attempting to communicate with the payment iframe hard code
    // the card token to an empty string as we know we will never actually need
    // a card token here and we want the shape of the data to be consistent
    const Card_Token = "";
    const paymentValuesWithToken = {
        ...paymentValues,
        Card_Token,
    };

    // Navigate to ReceiptPage
    useEffect(() => {
        if (toReceipt) {
            navigate(`${baseUrl}/${receiptId}/receipt`);
            dispatch(updateCurrentPage(STANDARD_REGISTRATION_RECEIPT));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toReceipt]);

    const sessionStorageKey = getSessionStorageKey(formId);

    const handleSuccess = (response) => {
        const { data } = response;
        const { success } = data;
        // 3. Set `submitting` to false
        setSubmitting(false);
        // 4. Handle response if successful
        if (success) {
            // remove form data from session storage
            // comment out below line for local testing so that data persists in sessionStorage
            asyncSessionStorage.remove(sessionStorageKey);
            const {
                response: {
                    registrationAccount,
                    feeCoverage,
                    transaction,
                    total,
                },
            } = success;
            const receiptIdFromTransaction = transaction.receiptId;
            const receipt = {
                feeCoverage,
                total,
                transaction,
            };
            // Store registration account data in redux store
            dispatch(updateRegistrationAccount(registrationAccount));
            // Store receipt data in redux store
            dispatch(fetchReceipt({ receipt }));
            // Update flag used by analytics logic
            dispatch(setAppHasSubmittedRegistration(true));
            // Navigate user to receipt
            setReceiptId(receiptIdFromTransaction);
            setToReceipt(true);
            window.scrollTo(0, 0);
        }
    };

    const handleFailure = (error) => {
        const {
            outdated = [],
            erroredEntities = {},
            message = ["An error occurred"],
        } = error;
        if (typeof message === "string") {
            const messageData = [];
            messageData.push(message);
            // formik
            setErrors(messageData);
        } else {
            // formik
            setErrors(message);
        }
        // formik
        setSubmitting(false);
        // this should not run when there are items in store cart
        // which is why we are not checking for updated
        if (outdated.length > 0) {
            dispatch(
                setOutdated({
                    formId,
                    outdated: [...outdated],
                }),
            );
        }
        if (erroredEntities.length > 0) {
            dispatch(
                setErroredEntities({
                    formId,
                    erroredEntities,
                }),
            );
        }
    };

    useEffect(() => {
        // 1. Assemble data for form submission
        const submitValues = prepareValuesForP2PRegistrationSubmit(
            settings,
            classification,
            paymentValuesWithToken,
            discounts,
            promoCodes,
            registrations,
            storeCart,
            parentRegistrationId,
            shippingMethodAmount,
            totals,
        );

        asyncSessionStorage
            .set(sessionStorageKey, sessionStorageReduxData)
            .then(() => {
                // 2. Send data to form submission endpoint
                submitRegistration(formId, submitValues)
                    .then((response) => handleSuccess(response))
                    .catch((error) => handleFailure(error));
            })
            .catch(() => {
                // need to call submitRegistration in catch block
                // since this will throw an error if we don't have
                // access to session storage. This is the expected
                // behavior when in Incognito mode on embedded forms on external sites.
                submitRegistration(formId, submitValues)
                    .then((response) => handleSuccess(response))
                    .catch((error) => handleFailure(error));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

EarlyRegistrationSubmit.propTypes = {
    baseUrl: PropTypes.string.isRequired,
    sessionStorageReduxData: PropTypes.shape({}).isRequired,
    settings: PropTypes.shape({}),
    classification: PropTypes.shape({}),
    discounts: PropTypes.shape({}),
    promoCodes: PropTypes.arrayOf(PropTypes.string),
    registrations: PropTypes.arrayOf(PropTypes.shape({})),
    formId: PropTypes.number.isRequired,
    storeCart: PropTypes.arrayOf(PropTypes.shape({})),
    shippingMethodAmount: PropTypes.number,
    parentRegistrationId: PropTypes.string,
    setSubmitting: PropTypes.func.isRequired,
    fetchReceipt: PropTypes.func.isRequired,
    setAppHasSubmittedRegistration: PropTypes.func.isRequired,
    setOutdated: PropTypes.func.isRequired,
    setErroredEntities: PropTypes.func.isRequired,
    updateRegistrationAccount: PropTypes.func.isRequired,
    setErrors: PropTypes.func.isRequired,
    totals: PropTypes.shape({}),
};

export default EarlyRegistrationSubmit;
